import { all, call, fork, takeEvery, put } from 'redux-saga/effects';

import * as layoutConstants from '../../constants';
import {
  changeLayoutWidth as changeLayoutWidthAction,
  changeSidebarTheme as changeLeftSidebarThemeAction,
  changeSidebarType as changeSidebarTypeAction,
} from './actions';
import { LayoutActions } from './constants';

/**
 * Changes the body attribute
 */
function changeBodyAttribute(attribute: string, value: string) {
  if (document.body) document.body.setAttribute(attribute, value);
  return true;
}

/**
 * Toggle the class on body
 * @param {*} cssClass
 */
function manageBodyClass(cssClass: string, action = 'toggle') {
  switch (action) {
    case 'add':
      if (document.body) document.body.classList.add(cssClass);
      break;
    case 'remove':
      if (document.body) document.body.classList.remove(cssClass);
      break;
    default:
      if (document.body) document.body.classList.toggle(cssClass);
      break;
  }

  return true;
}

type changeLayoutArgs = { payload: string; type: string };
function* changeLayout({ payload: layout }: changeLayoutArgs) {
  try {
    yield call(changeBodyAttribute, 'data-layout', layout);
    if (layout === layoutConstants.LAYOUT_VERTICAL) {
      yield put(changeLeftSidebarThemeAction(layoutConstants.LEFT_SIDEBAR_THEME_DEFAULT));
      yield put(changeSidebarTypeAction(layoutConstants.LEFT_SIDEBAR_TYPE_FIXED));
    }

    if (layout === layoutConstants.LAYOUT_HORIZONTAL) {
      yield put(changeLeftSidebarThemeAction(layoutConstants.LEFT_SIDEBAR_THEME_DEFAULT));
      yield put(changeSidebarTypeAction(layoutConstants.LEFT_SIDEBAR_TYPE_FIXED));
    }

    if (layout === layoutConstants.LAYOUT_DETACHED) {
      yield put(changeLayoutWidthAction(layoutConstants.LAYOUT_WIDTH_FLUID));
      yield put(changeSidebarTypeAction(layoutConstants.LEFT_SIDEBAR_TYPE_SCROLLABLE));
      yield put(changeLeftSidebarThemeAction(layoutConstants.LEFT_SIDEBAR_THEME_DEFAULT));
    }
  } catch (error) {
    console.error(error);
  }
}

type changeLayoutWidthArgs = { payload: string; type: string };
function* changeLayoutWidth({ payload: width }: changeLayoutWidthArgs) {
  try {
    yield call(changeBodyAttribute, 'data-layout-mode', width);
  } catch (error) {
    console.error(error);
  }
}

type changeLeftSidebarThemeArgs = { payload: string; type: string };
function* changeLeftSidebarTheme({ payload: theme }: changeLeftSidebarThemeArgs) {
  try {
    yield call(changeBodyAttribute, 'data-leftbar-theme', theme);
  } catch (error) {
    console.error(error);
  }
}

type changeLeftSidebarTypeArgs = { payload: string; type: string };
function* changeLeftSidebarType({ payload: type }: changeLeftSidebarTypeArgs) {
  try {
    yield call(changeBodyAttribute, 'data-leftbar-compact-mode', type);
  } catch (error) {
    console.error(error);
  }
}

/**
 * Toggles the rightsidebar
 */
function* toggleRightSidebar() {
  try {
    yield call(manageBodyClass, 'end-bar-enabled');
  } catch (error) {
    console.error(error);
  }
}

/**
 * Show the rightsidebar
 */
function* showRightSidebar() {
  try {
    yield call(manageBodyClass, 'end-bar-enabled', 'add');
  } catch (error) {
    console.error(error);
  }
}

/**
 * Hides the rightsidebar
 */
function* hideRightSidebar() {
  try {
    yield call(manageBodyClass, 'end-bar-enabled', 'remove');
  } catch (error) {
    console.error(error);
  }
}

/**
 * Watchers
 */
export function* watchChangeLayoutType() {
  yield takeEvery(LayoutActions.CHANGE_LAYOUT, changeLayout);
}

export function* watchChangeLayoutWidth() {
  yield takeEvery(LayoutActions.CHANGE_LAYOUT_WIDTH, changeLayoutWidth);
}

export function* watchChangeLeftSidebarTheme() {
  yield takeEvery(LayoutActions.CHANGE_SIDEBAR_THEME, changeLeftSidebarTheme);
}

export function* watchChangeLeftSidebarType() {
  yield takeEvery(LayoutActions.CHANGE_SIDEBAR_TYPE, changeLeftSidebarType);
}

export function* watchToggleRightSidebar() {
  yield takeEvery(LayoutActions.TOGGLE_RIGHT_SIDEBAR, toggleRightSidebar);
}

export function* watchShowRightSidebar() {
  yield takeEvery(LayoutActions.SHOW_RIGHT_SIDEBAR, showRightSidebar);
}

export function* watchHideRightSidebar() {
  yield takeEvery(LayoutActions.HIDE_RIGHT_SIDEBAR, hideRightSidebar);
}

function* LayoutSaga() {
  yield all([
    fork(watchChangeLayoutType),
    fork(watchChangeLayoutWidth),
    fork(watchChangeLeftSidebarTheme),
    fork(watchChangeLeftSidebarType),
    fork(watchToggleRightSidebar),
    fork(watchShowRightSidebar),
    fork(watchHideRightSidebar),
  ]);
}

export default LayoutSaga;
